import React from 'react';
import { object, string, bool, shape } from 'prop-types';

import cloneDeep from 'lodash/cloneDeep';

import CategoryTrendCarousel from '../category-trend-carousel';
import CategoryTrendContainer from '../../category-trend-container/category-trend-container';
import ContainerItems from '../../../components/grid-layout/container-items.desktop';
import { trendsList } from './constants';

/**
 * @typedef {Object} RenderDesktopContentProps
 * @property {Object} [trendsData] - Las propiedades de la tendencia.
 * @property {string} [trendsData.id] - El identificador de la tendencia.
 * @property {Object} [trendsData.trends_categories] - Las categorías de tendencias asociadas.
 * @property {boolean} [hasCarousel] - Indica si se debe mostrar un carrusel.
 * @property {boolean} [preload] - Indica si se deben precargar los elementos.
 * @property {string} [deviceType] - El tipo de dispositivo (por ejemplo, "desktop" o "mobile").
 * @property {Object} [countryConfig] - La configuración del país.
 */

/**
 * Componente que renderiza contenido de tendencias para escritorio basado en el identificador de la tendencia.
 *
 * @param {RenderDesktopContentProps} props - Las propiedades del componente.
 * @returns {React.ReactNode|null} El contenido de tendencias renderizado o `null` si no hay datos.
 */
const RenderDesktopContent = ({ trendsData, hasCarousel, preload, deviceType, countryConfig }) => {
  if (!trendsData) {
    return null;
  }

  const { id, trends_categories: trends } = trendsData;
  const trends_categories = trends ? cloneDeep(trends) : trends;

  switch (id) {
    case trendsList.HIGHER_GROWTH_TREND:
    case trendsList.MOST_WANTED_TREND:
    case trendsList.MOST_POPULAR_TREND:
    case trendsList.MANUAL_TRENDS:
      return <CategoryTrendCarousel key={`${id}-trend`} trends_categories={trends_categories} />;
    case trendsList.CONSOLIDATED_TRENDS:
      return <CategoryTrendContainer key={`${id}-trend`} trends_categories={trends_categories} />;
    case trendsList.OTHER_FILLED_TRENDS:
      return (
        <CategoryTrendContainer key={`${id}-trend`} trends_categories={trends_categories} hasCarousel={hasCarousel} />
      );
    case trendsList.BEST_SELLERS_POLYCARDS:
    case trendsList.RECOMMENDED_ITEMS_POLYCARDS:
      return (
        <ContainerItems
          key={`${id}-trend`}
          component={trends_categories}
          preload={preload}
          deviceType={deviceType}
          countryConfig={countryConfig}
          preloadNumImage={3}
        />
      );

    default:
      return null;
  }
};

RenderDesktopContent.propTypes = {
  countryConfig: object,
  deviceType: string,
  hasCarousel: bool,
  preload: bool,
  trendsData: shape({
    id: string,
    trends_categories: object,
  }),
};

export default RenderDesktopContent;
